import Vue from 'vue'
import store from '../store'
import CoursePlanStore from 'client/js/models/course-plan-store'
import SavedCoursePlan from 'common/models/saved-course-plan'

function getNewStateObj() {
   return {
      validationStates: {
         courseValid: true,
         preferencesValid: true,
         unitsValid: true,
         planValid: true,
         networkError: false
      },
      savedPlan: null,
      missingUnits: [],
   }
}

const getters = {
   getSavedCoursePlan: state => {
         return state.savedPlan
   },

   getValidationStates: state => {
      return state.validationStates
   },

   getMissingUnits: state => {
      return state.missingUnits
   },

   getSavedSelectedItems: state => {
      let savedSelectedItems = []
      if (state.savedPlan) {
         for (let yearIndex in state.savedPlan.coursePlan.years) {
            let year = state.savedPlan.coursePlan.years[yearIndex]
            for (let teachingPeriodIndex in year.teachingPeriods) {
               let teachingPeriod = year.teachingPeriods[teachingPeriodIndex]
               for (let unitIndex in teachingPeriod.units) {
                  let unit = teachingPeriod.units[unitIndex]
                  savedSelectedItems.push(unit)
               }
            }
         }
         //for each units in nonScheduled (completed units)
         state.savedPlan.coursePlan.nonScheduledUnits.map(unit => savedSelectedItems.push(unit))

         //for each units in siloed (missing pre-req or offering)
         state.savedPlan.coursePlan.siloedUnits.map(unit => savedSelectedItems.push(unit))
      }
      return savedSelectedItems
   },

   // returns currently selected unit codes
   getSavedSelectedUnitCodes:  (state, getters) => {
      var selectedItems = getters['getSavedSelectedItems'] || [];
      return selectedItems.map(x => x.code);
   }
}

const mutations = {

   updateUnitErrors(state, { errorList }) {
      errorList.forEach((error) => {
         let unit = state.savedPlan.coursePlan.years[error.refs.year].teachingPeriods[error.refs.teachingPeriod].units[error.refs.unit]
         if (!unit.errors) Vue.set(unit, 'errors', [])
         unit.errors.push(error.name)
      })
   },

   setSavedCoursePlanValid(state, valid)
   {
      if (state.savedPlan)
         state.savedPlan.validated = valid;
   },

   setValidationStates(state, states) {
      state.validationStates = states;
   },

   setSavedCoursePlan(state, plan) {
      state.savedPlan = plan;
   },

   setMissingUnits(state, missingUnits) {
      state.missingUnits = missingUnits
   },

   resetState: (state) => {
      Object.assign(state, getNewStateObj())
   }
}

const actions = {

   async addMissingUnit({commit, getters}, unit) {
      let missingUnits = getters['getMissingUnits']

      if (!missingUnits.includes(unit)) missingUnits.push(unit)

      commit['setMissingUnits', missingUnits]
   },

   async fetchSavedCoursePlan({ commit, getters, dispatch}, { id }) {
      //reset the saved state to clear out and load the new course.
      commit('resetState')
      store.commit('courses/resetState')
      store.commit('preferences/resetState')
      store.commit('units/resetState')
      store.commit('plans/resetState')

      //create the temp variable for the plan;
      let planData = null;

      //load the plan by id from the server side components.
      const response = await CoursePlanStore.load(id)
      if (response && response.response) {
         planData = response.response
         console.log('LOADED PLAN', planData)
      }

      //inject the following variables into the plan.
      if (!planData) {
        throw new Error("ID does not exist")
      }

      let plan = planData
      plan.planId = id;
      plan.validated = false

      // set course include drafts mode
      store.commit('courses/setIncludeDrafts', plan.incDrafts || false)

      //fake set data object.
      commit("setSavedCoursePlan", plan)
   },

   async storeCoursePlan({ commit, getters, rootCommit, rootGetters}) {
      //construct the plan from the various getters / setters and selected objects
      let plan = new SavedCoursePlan();

      // plan needs courseId, courseCode, courseName using course/getSelectedCourse
      const selectedCourse = await rootGetters['courses/getSelectedCourse']
      plan.courseId = selectedCourse.id
      plan.courseCode = selectedCourse.code
      plan.courseName = selectedCourse.name
      plan.courseLoopVersion = selectedCourse.courseLoopVersionNumber
      plan.status = selectedCourse.status
      plan.incDrafts = await rootGetters['courses/includeDraftVersions'] || false

      // selectedPreferences { studentType, placeOfResidence, studyOption, ex...}  using preferences/getSelectedStudyPreferences
      const selectedPreferences = await rootGetters['preferences/getSelectedStudyPreferences']
      plan.selectedPreferences.studentType = selectedPreferences.studentType
      plan.selectedPreferences.placeOfResidence = selectedPreferences.placeOfResidence
      plan.selectedPreferences.studyOption = selectedPreferences.studyOption
      plan.selectedPreferences.existingStudent = selectedPreferences.existingStudent
      plan.selectedPreferences.commencementYear = selectedPreferences.commencementYear
      plan.selectedPreferences.startYear = selectedPreferences.startYear
      plan.selectedPreferences.teachingPeriod = selectedPreferences.teachingPeriod
      plan.selectedPreferences.summerWinter = selectedPreferences.summerWinter

      // selectedUnits
      const selectedUnits = await rootGetters['units/getSelectedCurriculumItems']
      plan.selectedUnits = selectedUnits.map((unit) => unit.location)

      // selectedMajors
      const selectedMajors = selectedUnits.filter((unit) => unit.relationship.type === 'Major')
      plan.selectedMajors =  selectedMajors.map((major) => { return {code: major.relationship.code, name: major.relationship.name}})

      // selectedMinors
      const selectedMinors = selectedUnits.filter((unit) => unit.relationship.type === 'Minor')
      plan.selectedMinors = selectedMinors.map((minor) => { return {code: minor.relationship.code, name: minor.relationship.name}})

      // completedUnits
      const completedUnits = await rootGetters['units/getCompletedUnits']
      plan.completedUnits = completedUnits

      // requiredCreditPoints
      plan.totalCreditPoints = selectedCourse.requiredCredits

      // selectedCreditPoints
      let selectedCreditPoints = rootGetters['units/getTotalCredits']('root', false, false)
      plan.selectedCreditPoints = selectedCreditPoints

      // coursePlan
      const coursePlan = await rootGetters['plans/getPlanData']
      for (let yearIndex in coursePlan.years) {
         let year = coursePlan.years[yearIndex]
         for (let teachingPeriodIndex in year.teachingPeriods) {
            let teachingPeriod = year.teachingPeriods[teachingPeriodIndex]
            for (let unitIndex in teachingPeriod.units) {
               let unit = teachingPeriod.units[unitIndex]
               unit.name = selectedUnits.find((item) => item.id === unit.id).relationship.name
            }
         }
      }

      // setting the name for nonScheduledUnits
      for (let unitIndex in coursePlan.nonScheduledUnits) {
         let unit = coursePlan.nonScheduledUnits[unitIndex]
         unit.name = selectedUnits.find((item) => item.id === unit.id).relationship.name
      }

      // setting the name for siloedUnits
      for (let unitIndex in coursePlan.siloedUnits) {
         let unit = coursePlan.siloedUnits[unitIndex]
         unit.name = selectedUnits.find((item) => item.id === unit.id).relationship.name
      }

      plan.coursePlan = coursePlan

      // save this object to Azure, and then load it from the azure instance (ensuring valid object)
      // save the plan and return id from the server side components.
      let savedCoursePlanId = null
      const response = await CoursePlanStore.save(plan)
      if (response && response.response) {
         savedCoursePlanId = response.response
      }

      return savedCoursePlanId
   }
}

export default {
   namespaced: true,
   state: getNewStateObj(),
   getters,
   actions,
   mutations,
   getNewStateObj
}
